import React, { useState, useEffect, useCallback } from 'react';
import Builder3D from '../Builder3D';
import View2D from '../view/View2D';
import { groupCabinetsByFurniture } from '../utils/groupCabinetsByFurniture';

// Suvestinė (PDF ekspertizė) naudoja šiuos numatytuosius, didelius matmenis —
// ten kokybė svarbi (spausdinama/dokumentas). /baldai/:code puslapis (žr.
// FurnitureDrawingTabs.jsx) perduoda gerokai mažesnius per captureSize
// parametrą: šis fiksavimas paleidžiamas AUTOMATIŠKAI, be vartotojo veiksmo,
// vos tik baldas užsikrauna, o kelis 1500×1050 WebGL kontekstus vienas po kito
// atidaryti mobiliame Safari yra sunkiai keliama atminties apkrova — būtent tai
// ir sukeldavo puslapio "užsikrauna, tada po kelių sekundžių išsijungia" avariją.
const DEFAULT_CAPTURE_3D_WIDTH = 1500;
const DEFAULT_CAPTURE_3D_HEIGHT = 1050;
const DEFAULT_CAPTURE_2D_WIDTH = 1700;
const DEFAULT_CAPTURE_2D_HEIGHT = 1050;

const noop = () => {};

// Kartais r3f Canvas vidinis įvykių prijungimo mechanizmas ("events.connect")
// susikerta su dažnu off-screen egzempliorių persipiešimu ir meta netikėtą
// "Cannot read properties of null (reading 'addEventListener')" — tai NĖRA
// mūsų duomenų klaida, tik reta r3f vidinė lenktynė. Vietoj to, kad visas
// puslapis sudužtų, šis boundary tyliai "praryja" klaidą (fiksavimas tiesiog
// praleidžia tą vieną taikinį — onFail toliau perjungia į kitą).
class CaptureErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(error) {
        console.warn('Brėžinio fiksavimas nepavyko, praleidžiama:', error?.message);
        this.props.onFail?.();
    }
    componentDidUpdate(prevProps) {
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false });
        }
    }
    render() {
        return this.state.hasError ? null : this.props.children;
    }
}

// Sudaro fiksavimo eiliės sąrašą: "patalpa" (visi baldai su siena, tik jei baldų >1),
// tada kiekvienam baldo vienetui — "išorė" (uždaros durys, be sienos) ir "vidus"
// (durys paslėptos, vidinė struktūra matoma, be sienos). "Baldas" skiltis Suvestinėje
// pakartotinai naudoja "išorė" nuotrauką — statiškai jos atrodo identiškai (žr.
// SingleCabinet3D activeStep logiką), skirtumas tarp jų yra tik redagavimo galimybėse,
// kurios statiniame dokumente neaktualios.
// includeProduction — NAUJAS "production" (gamybinio brėžinio) taikinys yra
// SĄMONINGAI opt-in, ne pridedamas visiems esamiems šio hook'o naudotojams
// automatiškai: AssemblyInstructionsModal.jsx ir SummaryView.jsx abu kviečia
// šį hook'ą BE `neededKeys` (fiksuoja viską nedelsiant) — jei "production"
// taikinys būtų pridedamas besąlygiškai, AssemblyInstructionsModal (kuris
// niekada nenaudoja gamybinio brėžinio) veltui atliktų papildomą 2D
// fiksavimo praėjimą kiekvienam baldo vienetui. Tik SummaryView.jsx (žr.
// jos useFurnitureDrawings iškvietimą) paduoda includeProduction=true.
export const buildCaptureTargets = (groups, includeProduction = false) => {
    const targets = [];
    if (groups.length > 1) targets.push({ key: 'room', kind: 'room', furnitureId: null });
    groups.forEach(g => {
        targets.push({ key: `${g.furnitureId}-exterior`, kind: 'exterior', furnitureId: g.furnitureId });
        targets.push({ key: `${g.furnitureId}-interior`, kind: 'interior', furnitureId: g.furnitureId });
        // "production" — kiekvieno fizinio skydelio išklotinė su furnitūros
        // gręžimo skylėmis (žr. View2D.jsx productionMode). Grynas 2D
        // brėžinys, be jokio 3D atitikmens — fiksavimo fazių mašina žemiau
        // šiam kind'ui PRALEIDŽIA '3d' fazę visiškai (niekada nemontuoja
        // Builder3D), tad rezultatas vis tiek atsiduria TAME PAČIAME
        // images[key].img2d lauke, kaip ir kitiems 2D vaizdams.
        if (includeProduction) targets.push({ key: `${g.furnitureId}-production`, kind: 'production', furnitureId: g.furnitureId });
    });
    return targets;
};

// react-three-fiber renders each <Canvas> through its OWN independent reconciler
// tree (separate from the surrounding DOM React tree), so a normal React
// ErrorBoundary wrapping our off-screen <Canvas> instances can't catch errors
// thrown inside it. When this hook is the very first Canvas mounted on a page
// (true for /baldai/:code, which has no other 3D view already on screen), r3f's
// own internal pointer-event connect() occasionally races during the rapid
// mount/unmount of these off-screen instances and throws
// "Cannot read properties of null (reading 'addEventListener')" — harmless
// (these instances never receive real pointer input, and captures still
// succeed), but noisy: it trips CRA's dev-only error overlay and would count
// as an uncaught window error. Swallow just this one known signature.
const isBenignR3fConnectError = (message) => (
    typeof message === 'string'
    && message.includes("reading 'addEventListener'")
);

const useFurnitureDrawings = (active, groups, settings, captureSize, neededKeys, includeProduction = false) => {
    const capture3dWidth = captureSize?.width3d ?? DEFAULT_CAPTURE_3D_WIDTH;
    const capture3dHeight = captureSize?.height3d ?? DEFAULT_CAPTURE_3D_HEIGHT;
    const capture2dWidth = captureSize?.width2d ?? DEFAULT_CAPTURE_2D_WIDTH;
    const capture2dHeight = captureSize?.height2d ?? DEFAULT_CAPTURE_2D_HEIGHT;

    const [images, setImages] = useState({});
    const [captureIndex, setCaptureIndex] = useState(0);
    // 3D (Builder3D) IR 2D (View2D) abu naudoja react-three-fiber <Canvas> — t.y.
    // KIEKVIENAS yra savo WebGL kontekstas. Anksčiau abu buvo montuojami VIENU METU
    // vienam taikiniui — kartu su jau matomu pagrindiniu 3D vaizdu tai duodavo iki 3
    // vienu metu atidarytus WebGL kontekstus, o mobiliuose telefonuose (ir kai kuriose
    // headless aplinkose su programine WebGL realizacija) tai sukeldavo
    // "THREE.WebGLRenderer: Context Lost" — naršyklė priverstinai atima kontekstą, kad
    // atlaisvintų vietos. Dabar 3D ir 2D fiksuojami GRIEŽTAI NUOSEKLIAI (capturePhase) —
    // bet kuriuo metu yra sumontuotas TIK VIENAS papildomas paslėptas kontekstas.
    const [capturePhase, setCapturePhase] = useState('3d'); // '3d' | '2d'

    useEffect(() => {
        if (!active) return undefined;
        const handler = (event) => {
            if (isBenignR3fConnectError(event.message)) event.preventDefault();
        };
        window.addEventListener('error', handler);
        return () => window.removeEventListener('error', handler);
    }, [active]);

    // neededKeys (jei perduota) apriboja fiksavimą TIK tais taikiniais, kurių
    // realiai reikia dabar (žr. FurnitureDrawingTabs.jsx) — kiekvienas taikinys
    // vis tiek atidaro savo WebGL kontekstą, tad fiksuoti "Vidus" vaizdą ar kitą
    // baldo vienetą, kurio vartotojas net nepamatė, buvo nereikalinga apkrova
    // (dar viena context-churn priežastis, žr. capturePhase pastabą aukščiau).
    // Kai neededKeys nepaduota (pvz. Suvestinė), elgesys lieka toks pat kaip anksčiau —
    // fiksuojama iš karto viskas.
    const allTargets = active ? buildCaptureTargets(groups, includeProduction) : [];
    const targets = neededKeys ? allTargets.filter(t => neededKeys.includes(t.key)) : allTargets;

    useEffect(() => {
        if (!active) return;
        setImages({});
        setCaptureIndex(0);
        setCapturePhase('3d');
    }, [active]);

    const currentTarget = active && captureIndex < targets.length ? targets[captureIndex] : null;
    const currentTargetKey = currentTarget?.key ?? null;

    // buildCaptureTargets() (per allTargets aukščiau) kuria NAUJUS objekto literalus
    // KIEKVIENAME render'e — tad currentTarget (targets[captureIndex]) yra NAUJA
    // OBJEKTO NUORODA kas render'ą, net kai realus taikinys nepasikeitė. Jei šis
    // efektas priklausytų nuo PATIES OBJEKTO (currentTarget), React'o Object.is
    // palyginimas jį matytų kaip "pasikeitusį" KIEKVIENĄ render'ą — įskaitant TĄ
    // PATĮ commit'ą, kuriame handle3dReady ką tik perjungė capturePhase į '2d' —
    // ir šis efektas nedelsiant grąžindavo atgal į '3d', View2D taip ir nespėdamas
    // sumontuoti/nufiksuoti. Todėl priklausomybė — stabilus STRING raktas, ne objektas.
    // "production" taikiniai (žr. buildCaptureTargets) neturi jokio 3D
    // atitikmens — jiems iš karto pradedama nuo '2d' fazės, praleidžiant
    // Builder3D montavimą visiškai.
    useEffect(() => {
        setCapturePhase(currentTarget?.kind === 'production' ? '2d' : '3d');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTargetKey]);

    const handle3dReady = useCallback((gl) => {
        if (currentTarget) {
            try {
                const url = gl.domElement.toDataURL('image/png');
                setImages(prev => ({ ...prev, [currentTarget.key]: { ...prev[currentTarget.key], img3d: url } }));
            } catch { /* WebGL context lost — skip this drawing */ }
        }
        // Atlaisviname šį WebGL kontekstą IŠKART, o ne pasikliaujame unmount/GC
        // laiku — image duomenys jau ištraukti aukščiau. Nuoseklus fiksavimas
        // (žr. capturePhase pastabą) jau apriboja vienu metu atidarytų kontekstų
        // skaičių iki 1, bet greitas mount/unmount ciklas per daug taikinių
        // (kelios spintos su daug sekcijų) vis tiek galėjo palikti kontekstus
        // "kaboti" iki GC, todėl VĖLIAU montuojamas gyvo redaktoriaus 2D/3D
        // Canvas kartais likdavo tuščias (žr. /admin/bugs "Neveikia ir nerodo
        // 2D vaizdo").
        try { gl.dispose?.(); gl.forceContextLoss?.(); } catch { /* ignore */ }
        setCapturePhase('2d');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTargetKey]);

    const handle2dReady = useCallback((gl) => {
        if (currentTarget) {
            try {
                const url = gl.domElement.toDataURL('image/png');
                setImages(prev => ({ ...prev, [currentTarget.key]: { ...prev[currentTarget.key], img2d: url } }));
            } catch { /* WebGL context lost — skip this drawing */ }
        }
        try { gl.dispose?.(); gl.forceContextLoss?.(); } catch { /* ignore */ }
        setCaptureIndex(i => i + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTargetKey]);

    // Saugiklis — jei per 9s einamas fazė (3D arba 2D) taip ir neatsiliepė, vseik
    // pereinama toliau, kad procesas neužstrigtų amžinai. Priklausomybė — stabilus
    // currentTargetKey (ne currentTarget objektas) tuo pačiu paaiškinimu kaip
    // aukščiau: priešingu atveju šis efektas persileisdavo (clearTimeout+naujas
    // setTimeout) KIEKVIENĄ render'ą, ir 9s atgalinis skaičiavimas taip ir
    // niekada realiai nesuspėdavo suveikti.
    useEffect(() => {
        if (!currentTarget) return undefined;
        const t = setTimeout(() => {
            if (capturePhase === '3d') setCapturePhase('2d');
            else setCaptureIndex(i => i + 1);
        }, 9000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTargetKey, capturePhase]);

    let hiddenNode = null;
    if (currentTarget) {
        const isRoom = currentTarget.kind === 'room';
        const isProduction = currentTarget.kind === 'production';
        const group = isRoom ? null : groups.find(g => g.furnitureId === currentTarget.furnitureId);
        const cabinetsForTarget = isRoom ? (settings.cabinets || []) : (group?.cabinets || []);
        const activeStep3D = isRoom ? 'Patalpa' : (currentTarget.kind === 'interior' ? 'Vidus' : 'Išorė');
        const hideWall = !isRoom;
        const localGroupRects = isRoom
            ? []
            : groupCabinetsByFurniture(cabinetsForTarget).map(g => ({
                x: g.x, y: g.y, z: 0, width: g.width, height: g.height, depth: g.depth, furnitureId: g.furnitureId,
            }));

        hiddenNode = (
            <CaptureErrorBoundary resetKey={`${currentTarget.key}-${capturePhase}`} onFail={() => {
                if (capturePhase === '3d') setCapturePhase('2d'); else setCaptureIndex(i => i + 1);
            }}>
                {capturePhase === '3d' && !isProduction && (
                    <div data-testid="fitit-hidden-capture-3d" style={{ position: 'fixed', left: -99999, top: 0, width: capture3dWidth, height: capture3dHeight, background: '#fff' }}>
                        <Builder3D
                            frameKey={currentTarget.key}
                            cabinets={cabinetsForTarget}
                            setCabinets={noop}
                            backWall={settings.backWall}
                            setBackWall={noop}
                            viewMode="3D"
                            setViewMode={noop}
                            activeFurnitureId={isRoom ? null : currentTarget.furnitureId}
                            onSelect={noop}
                            onMove={noop}
                            activeStep={activeStep3D}
                            wallWidth={settings.wallWidth}
                            wallHeight={settings.wallHeight}
                            wallDepth={settings.wallDepth}
                            wallDepthRight={settings.wallDepthRight}
                            basisType={settings.basisType}
                            basisHeight={settings.basisHeight}
                            selectedPlate={settings.selectedPlate}
                            sectionOptions={settings.sectionOptions}
                            mergedSections={settings.mergedSections}
                            doorPlates={settings.doorPlates}
                            obstacles={isRoom ? settings.obstacles : []}
                            selectedDoorPlate={settings.selectedDoorPlate}
                            doorColors={settings.doorColors}
                            setDoorColors={noop}
                            allFurnitureGroupRects={localGroupRects}
                            coverPlates={settings.coverPlates}
                            roomShape={settings.roomShape}
                            skirtingType={settings.skirtingBoardCutting}
                            leftSideElement={group?.cabinets?.[0]?.leftSideElement ?? settings.leftSideElement}
                            rightSideElement={group?.cabinets?.[0]?.rightSideElement ?? settings.rightSideElement}
                            panelFrontOffsets={settings.panelFrontOffsets}
                            roomStyle3D={settings.roomStyle3D || 0}
                            hideWall={hideWall}
                            furnitureType={settings.type}
                            slidingDoorCount={settings.slidingDoorCount}
                            slidingDoorProfile={settings.slidingDoorProfile}
                            onFramesRendered={handle3dReady}
                        />
                    </div>
                )}
                {capturePhase === '2d' && (
                    <div data-testid="fitit-hidden-capture-2d" style={{ position: 'fixed', left: -99999, top: 0, width: capture2dWidth, height: capture2dHeight, background: '#fff' }}>
                        {/* Non-room targets (exterior/interior of ONE group): View2DScene's
                            camera-fit and SideView both size themselves off wallWidth/wallDepth/
                            wallDepthRight — passing the whole PROJECT's wall dimensions here (as
                            opposed to 3D capture, whose camera fits to the actual cabinet geometry
                            instead) made a single small section's drawing frame itself for the
                            whole multi-section furniture's width/depth, leaving a huge empty gap
                            and an oversized, wrongly-positioned side view. Scope to this group's
                            own footprint instead — for the single "room" target (whole multi-piece
                            layout with real walls) the project-wide values are still correct. */}
                        <View2D
                            frameKey={currentTarget.key}
                            allCabinets={cabinetsForTarget}
                            wallWidth={isRoom ? settings.wallWidth : (group?.width || 0) * 100}
                            wallHeight={isRoom ? settings.wallHeight : (group?.height || 0) * 100}
                            wallDepth={isRoom ? settings.wallDepth : (group?.depth || 0) * 100}
                            wallDepthRight={isRoom ? settings.wallDepthRight : (group?.depth || 0) * 100}
                            roomShape={isRoom ? settings.roomShape : 0}
                            sectionOptions={settings.sectionOptions}
                            doorColors={settings.doorColors}
                            mergedSections={settings.mergedSections}
                            doorHandles={settings.doorHandles}
                            furnitureType={settings.type}
                            slidingDoorCount={settings.slidingDoorCount}
                            defaultShowWall={isRoom}
                            hideInterior={!isRoom && currentTarget.kind === 'exterior'}
                            hideDoors={!isRoom && currentTarget.kind === 'interior'}
                            productionMode={isProduction}
                            onFramesRendered={handle2dReady}
                        />
                    </div>
                )}
            </CaptureErrorBoundary>
        );
    }

    return {
        images,
        capturing: !!currentTarget,
        hiddenNode,
        progress: { done: captureIndex, total: targets.length },
    };
};

export default useFurnitureDrawings;
